import { Stack } from '@fluentui/react';
import { IPivotItem, PageHeader, Pivot, useHaicPageTitle } from '@h2oai/ui-kit';

import { EntityType } from '../../aiem/entity/constants';
import { daiEngineProfileEntity } from '../../aiem/entity/DAIEngineProfile.entity';
import { daiEngineVersionEntity } from '../../aiem/entity/DAIEngineVersion.entity';
import { h2oEngineProfileEntity } from '../../aiem/entity/H2OEngineProfile.entity';
import { h2oEngineVersionEntity } from '../../aiem/entity/H2OEngineVersion.entity';
import { notebookEngineImageEntity } from '../../aiem/entity/NotebookEngineImage.entity';
import { notebookEngineProfileEntity } from '../../aiem/entity/NotebookEngineProfile.entity';
import { sandboxEngineImageEntity } from '../../aiem/entity/SandboxEngineImage.entity';
import { sandboxEngineTemplateEntity } from '../../aiem/entity/SandboxEngineTemplate.entity';
import { AIEMSettingsEntitiesMap } from '../../aiem/entity/services';
import { DAIEngineProfile } from '../../aiem/gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import { DAIEngineVersion } from '../../aiem/gen/ai/h2o/engine/v1/dai_engine_version_pb';
import { H2OEngineProfile } from '../../aiem/gen/ai/h2o/engine/v1/h2o_engine_profile_pb';
import { H2OEngineVersion } from '../../aiem/gen/ai/h2o/engine/v1/h2o_engine_version_pb';
import { NotebookEngineImage } from '../../aiem/gen/ai/h2o/engine/v1/notebook_engine_image_pb';
import { NotebookEngineProfile } from '../../aiem/gen/ai/h2o/engine/v1/notebook_engine_profile_pb';
import { SandboxEngineImage } from '../../aiem/gen/ai/h2o/engine/v1/sandbox_engine_image_pb';
import type { SandboxEngineTemplate } from '../../aiem/gen/ai/h2o/engine/v1/sandbox_engine_template_pb';
import { stackStylesNoLeftNav } from '../../themes/themes';
import { NotebookEntityDataContainer } from '../NotebookAdminSettingsPage/components/NotebookEntityDataContainer';
import EntityDataContainer from './components/EntityDataContainer';
import { ListWithCollapsedSettings } from './components/ListWithCollapsedSettings/ListWithCollapsedSettings';
import {
  DAIEngineProfileActions,
  DAIEngineVersionActions,
  H2OEngineProfileActions,
  H2OEngineVersionActions,
  NotebookEngineImageActions,
  NotebookEngineProfileActions,
  SandboxEngineImageActions,
  SandboxEngineTemplateActions,
} from './constants';

export default function AIEMAdminSettingsPage() {
  const items: IPivotItem[] = [
    {
      key: `pivot-item-content-default-dai-engine-profile`,
      headerText: 'DAI Engine Profiles',
      content: (
        <ListWithCollapsedSettings<DAIEngineProfile>
          entity={daiEngineProfileEntity}
          userActions={DAIEngineProfileActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-default-h2o-engine-profile`,
      headerText: 'H2O Engine Profiles',
      content: (
        <ListWithCollapsedSettings<H2OEngineProfile>
          entity={h2oEngineProfileEntity}
          userActions={H2OEngineProfileActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-default-notebook-engine-profile`,
      headerText: 'Notebook Engine Profiles',
      content: (
        <ListWithCollapsedSettings<NotebookEngineProfile>
          entity={notebookEngineProfileEntity}
          userActions={NotebookEngineProfileActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-default-sandbox-engine-template`,
      headerText: 'Sandbox Engine Templates',
      content: (
        <ListWithCollapsedSettings<SandboxEngineTemplate>
          entity={sandboxEngineTemplateEntity}
          userActions={SandboxEngineTemplateActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-dai-engine-version`,
      headerText: 'DAI Engine Version',
      content: (
        <EntityDataContainer<DAIEngineVersion, EntityType>
          apiKey="aiEngineManagerApiUrl"
          entity={daiEngineVersionEntity}
          entitiesMap={AIEMSettingsEntitiesMap}
          activeWorkspaceName="workspaces/global"
          userActions={DAIEngineVersionActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-h2o-engine-version`,
      headerText: 'H2O Engine Version',
      content: (
        <EntityDataContainer<H2OEngineVersion, EntityType>
          apiKey="aiEngineManagerApiUrl"
          entity={h2oEngineVersionEntity}
          entitiesMap={AIEMSettingsEntitiesMap}
          activeWorkspaceName="workspaces/global"
          userActions={H2OEngineVersionActions}
        />
      ),
      loading: false,
    },
    {
      key: `pivot-item-content-notebook-engine-image`,
      headerText: 'Notebook Engine Image',
      content: (
        <NotebookEntityDataContainer<NotebookEngineImage>
          entity={notebookEngineImageEntity}
          apiKey="aiEngineManagerApiUrl"
          userActions={NotebookEngineImageActions}
        />
      ),
    },
    {
      key: `pivot-item-content-sandbox-engine-image`,
      headerText: 'Sandbox Engine Image',
      content: (
        <NotebookEntityDataContainer<SandboxEngineImage>
          entity={sandboxEngineImageEntity}
          apiKey="aiEngineManagerApiUrl"
          userActions={SandboxEngineImageActions}
        />
      ),
    },
  ];

  useHaicPageTitle('AI Engine Manager Admin Settings');

  return (
    <Stack styles={stackStylesNoLeftNav}>
      <Stack styles={{ root: { position: 'relative' } }}>
        <Stack horizontal horizontalAlign="space-between" style={{ marginBottom: '20px' }}>
          <Stack.Item disableShrink>
            <PageHeader pageTitle="AI Engine Manager Admin Settings" />
          </Stack.Item>
        </Stack>
        <Pivot items={items} styles={{ root: { overflow: 'auto' } }} />
      </Stack>
    </Stack>
  );
}
